import {
  AgentMergeActivity_Phase as Phase,
  AgentMergeActivity_Outcome as Outcome,
  AgentMergeActivity_Reason as Reason,
  AgentMergeSession_Status as Status,
  AgentMergeStatusResponse_Selection as Selection,
  type AgentMergeActivity,
  type AgentMergeContext,
  type AgentMergePresentation,
  type AgentMergeScope,
  type AgentMergeSession,
  type AgentMergeStatusResponse,
} from '@github-ui/dotcom-schema/types/copilot_sessions/v1/agent_merge'

type Observation = {activity: AgentMergeActivity; invalidated: boolean}

export type ActivityAnnouncementSource = 'session' | 'activity' | 'evaluation'
export type ActivityState = {
  context?: AgentMergeContext
  session?: AgentMergeSession
  selection: Selection
  seen: Set<string>
  observations: Map<string, Observation>
  terminalJobs: Set<string>
}
export type ActivityPresentation = {
  row:
    | 'off'
    | 'baseline'
    | 'refreshing'
    | 'queued'
    | 'evaluating'
    | 'preparing'
    | 'starting'
    | 'running'
    | 'waiting_for_user'
    | 'idle'
    | 'completed'
    | 'failed'
    | 'cancelled'
    | 'timed_out'
    | 'session_unknown'
    | 'read_unavailable'
    | 'unavailable'
  detail?:
    | 'checks_pending'
    | 'retry_scheduled'
    | 'quota_exceeded'
    | 'access_denied'
    | 'attempt_limit'
    | 'steering_accepted'
  detailIdentity?: string
  job?: string
  attempt?: string
  goal?: string
  url?: string
  actions?: number[]
  deltaReasons?: number[]
  deltaActions?: number[]
  message?: AgentMergePresentation
  messageIdentity?: string
}

export function initialActivityState(): ActivityState {
  return {
    selection: Selection.UNKNOWN,
    seen: new Set(),
    observations: new Map(),
    terminalJobs: new Set(),
  }
}

export function sameScope(a?: AgentMergeScope, b?: AgentMergeScope) {
  return (
    !!a &&
    !!b &&
    a.namespace === b.namespace &&
    a.repositoryId === b.repositoryId &&
    a.pullRequestNumber === b.pullRequestNumber &&
    a.pullRequestId === b.pullRequestId
  )
}

export function isTerminal(status: Status) {
  return status >= Status.COMPLETED && status <= Status.TIMED_OUT
}

export function guardTerminalSession(session: AgentMergeSession | undefined, terminalJobs: ReadonlySet<string>) {
  return session && terminalJobs.has(session.jobId) && !isTerminal(session.status)
    ? {...session, status: Status.UNKNOWN}
    : session
}

export function compatible(context: AgentMergeContext | undefined, activity: AgentMergeActivity) {
  if (!context?.enabled) return false
  const generation = activity.currentConfigurationGeneration || activity.advisoryConfigurationGeneration || 0n
  return (
    !(generation && context.configurationGeneration && generation !== context.configurationGeneration) &&
    (!activity.headOid || !context.headOid || activity.headOid === context.headOid)
  )
}

export function invalidateActivity(state: ActivityState): ActivityState {
  return {
    ...state,
    observations: new Map([...state.observations].map(([key, value]) => [key, {...value, invalidated: true}])),
  }
}

export function hydrateActivity(state: ActivityState, response: AgentMergeStatusResponse): ActivityState {
  const incoming = response.context
  if (!incoming?.scope) return {...invalidateActivity(state), session: undefined, selection: Selection.UNAVAILABLE}
  if (
    sameScope(state.context?.scope, incoming.scope) &&
    incoming.configurationGeneration &&
    state.context?.configurationGeneration &&
    incoming.configurationGeneration < state.context.configurationGeneration
  ) {
    return state
  }
  const context = {
    ...incoming,
    configurationGeneration: incoming.configurationGeneration || state.context?.configurationGeneration,
    headOid: incoming.headOid || state.context?.headOid || '',
  }
  const changed =
    state.context &&
    (!sameScope(state.context.scope, context.scope) ||
      state.context.enabled !== context.enabled ||
      state.context.configurationGeneration !== context.configurationGeneration ||
      state.context.headOid !== context.headOid)
  const terminalJobs = new Set([...state.terminalJobs, ...response.terminalJobIds])
  let session = response.selection === Selection.SELECTED ? response.selectedSession : undefined
  if (
    session &&
    state.session?.jobId === session.jobId &&
    (isTerminal(state.session.status) ||
      (state.session.status === Status.RUNNING && [Status.PENDING, Status.QUEUED].includes(session.status)))
  ) {
    session = {...session, status: state.session.status}
  }
  session = guardTerminalSession(session, terminalJobs)
  if (session && isTerminal(session.status)) terminalJobs.add(session.jobId)
  return {
    ...state,
    context,
    selection: response.selection,
    session,
    terminalJobs,
    observations: new Map(
      [...state.observations].map(([key, observation]) => [
        key,
        changed || !compatible(context, observation.activity) ? {...observation, invalidated: true} : observation,
      ]),
    ),
  }
}

export function timestampMillis(value: AgentMergeActivity['occurredAt']): number {
  if (!value || value.seconds < 0n || value.seconds > 253402300799n || value.nanos < 0 || value.nanos >= 1_000_000_000)
    return Number.NaN
  return Number(value.seconds) * 1000 + value.nanos / 1_000_000
}

function valid(activity: AgentMergeActivity) {
  const occurred = timestampMillis(activity.occurredAt)
  const expires = timestampMillis(activity.expiresAt)
  const retry = timestampMillis(activity.retryAt)
  const expected =
    activity.phase === Phase.DEFERRED && activity.retryAt
      ? Math.min(retry + 30_000, occurred + 300_000)
      : occurred + 60_000
  return (
    activity.attempt >= 0 &&
    Number.isInteger(activity.attempt) &&
    activity.phase >= Phase.EVALUATION_QUEUED &&
    activity.phase <= Phase.STEERING_ACCEPTED &&
    activity.outcome >= 0 &&
    activity.outcome <= Outcome.CANCELLED &&
    (activity.outcome !== Outcome.JOB_CREATED || !!activity.jobId) &&
    (activity.phase !== Phase.EVALUATION_FINISHED || activity.outcome !== Outcome.OUTCOME_UNKNOWN) &&
    activity.reason >= 0 &&
    activity.reason <= Reason.CUSTOM_AGENT_UNAVAILABLE &&
    [...activity.authorizedActions, ...activity.deltaActions].every(action => action >= 1 && action <= 3) &&
    activity.deltaReasons.every(reason => reason >= 1 && reason <= 4) &&
    Number.isFinite(expected) &&
    expires > occurred &&
    Math.abs(expires - expected) < 1
  )
}

export function receiveActivity(
  state: ActivityState,
  scope: AgentMergeScope | undefined,
  activity: AgentMergeActivity,
): ActivityState {
  if (
    !sameScope(state.context?.scope, scope) ||
    !activity.transportScope ||
    !activity.evaluationId ||
    !activity.transitionId ||
    [
      activity.transitionId,
      activity.evaluationId,
      activity.transportScope,
      activity.jobId,
      activity.sessionId,
      activity.steeringCommandId,
    ].some(value => value && value.length > 512)
  )
    return state
  const identity = JSON.stringify([activity.transportScope, activity.transitionId])
  if (state.seen.has(identity)) return state
  const next = {...state, seen: new Set(state.seen).add(identity)}
  if (!valid(activity) || !compatible(state.context, activity)) return next
  const steering = activity.phase === Phase.STEERING_ACCEPTED
  if (steering && (!activity.jobId || !activity.steeringCommandId)) return next
  const key = JSON.stringify(
    steering
      ? [activity.transportScope, activity.jobId, activity.steeringCommandId]
      : [activity.transportScope, activity.evaluationId],
  )
  const previous = state.observations.get(key)
  if (
    previous &&
    (steering ||
      activity.attempt < previous.activity.attempt ||
      (activity.attempt === previous.activity.attempt && activity.phase <= previous.activity.phase))
  )
    return next
  return {...next, observations: new Map(state.observations).set(key, {activity, invalidated: false})}
}

function fresh(state: ActivityState, {activity, invalidated}: Observation, now: number) {
  return (
    !invalidated &&
    compatible(state.context, activity) &&
    timestampMillis(activity.expiresAt) > now &&
    (!activity.jobId || !state.terminalJobs.has(activity.jobId))
  )
}

export function presentActivity(state: ActivityState, now: number, showActivity = true): ActivityPresentation {
  const observations = showActivity
    ? [...state.observations]
        .filter(([, observation]) => fresh(state, observation, now))
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, observation]) => observation)
    : []
  const session = state.session
  let terminalSession: ActivityPresentation | undefined
  if (session) {
    const rows: Record<Status, ActivityPresentation['row']> = {
      [Status.UNKNOWN]: 'session_unknown',
      [Status.PENDING]: 'starting',
      [Status.QUEUED]: 'starting',
      [Status.RUNNING]: 'running',
      [Status.COMPLETED]: 'completed',
      [Status.FAILED]: 'failed',
      [Status.CANCELLED]: 'cancelled',
      [Status.TIMED_OUT]: 'timed_out',
      [Status.WAITING_FOR_USER]: 'waiting_for_user',
      [Status.IDLE]: 'idle',
    }
    const result: ActivityPresentation = {
      row: rows[session.status] ?? 'session_unknown',
      job: session.jobId,
      goal: session.goal,
      url: session.url,
    }
    if (isTerminal(session.status)) {
      terminalSession = result
    } else {
      for (const {activity} of observations) {
        if (activity.phase === Phase.STEERING_ACCEPTED && activity.jobId === session.jobId) {
          result.detail = 'steering_accepted'
          result.detailIdentity = JSON.stringify([
            'steering',
            activity.transportScope,
            activity.jobId,
            activity.steeringCommandId,
          ])
          result.deltaReasons = activity.deltaReasons
          result.deltaActions = activity.deltaActions
          break
        }
      }
      return result
    }
  }
  if (state.selection === Selection.UNAVAILABLE) return terminalSession ?? {row: 'unavailable'}
  if (!state.context?.enabled) return terminalSession ?? {row: 'off'}
  if (state.selection === Selection.UNKNOWN) return terminalSession ?? {row: 'unavailable'}
  let result: ActivityPresentation = {row: 'baseline'}
  let priority = 0
  for (const {activity} of observations) {
    let rank = 0
    let candidate: ActivityPresentation = {row: 'baseline'}
    if (activity.phase === Phase.PREPARING_SESSION) {
      rank = 4
      candidate = {row: 'preparing', actions: activity.authorizedActions}
    } else if (activity.phase === Phase.EVALUATING || activity.phase === Phase.EVALUATION_QUEUED) {
      rank = activity.phase === Phase.EVALUATING ? 3 : 2
      candidate.row = activity.phase === Phase.EVALUATING ? 'evaluating' : 'queued'
    } else if (activity.phase === Phase.DEFERRED) {
      rank = 1
      candidate.detail = 'retry_scheduled'
    } else if (activity.phase === Phase.EVALUATION_FINISHED) {
      if (activity.outcome === Outcome.JOB_CREATED && activity.jobId !== session?.jobId) {
        rank = 4
        candidate = {row: 'preparing', actions: activity.authorizedActions}
      } else if (activity.outcome === Outcome.WAITING && activity.reason === Reason.CHECKS_PENDING) {
        rank = 1
        candidate.detail = 'checks_pending'
      } else if (activity.outcome === Outcome.BLOCKED) {
        const reasons: Partial<Record<Reason, ActivityPresentation['detail']>> = {
          [Reason.QUOTA_EXCEEDED]: 'quota_exceeded',
          [Reason.ACCESS_DENIED]: 'access_denied',
          [Reason.ATTEMPT_LIMIT]: 'attempt_limit',
        }
        candidate.detail = reasons[activity.reason]
        rank = candidate.detail ? 1 : 0
      }
    }
    if (rank > priority) {
      if (candidate.detail) {
        candidate.detailIdentity = JSON.stringify([
          'evaluation',
          activity.transportScope,
          activity.evaluationId,
          activity.attempt,
        ])
      }
      if (rank >= 2) {
        candidate.attempt = JSON.stringify([activity.transportScope, activity.evaluationId, activity.attempt])
      }
      priority = rank
      result = candidate
    }
  }
  return priority >= 2 ? result : (terminalSession ?? result)
}

export function nextActivityDeadline(state: ActivityState, now: number) {
  let deadline = Infinity
  for (const observation of state.observations.values()) {
    if (fresh(state, observation, now)) {
      deadline = Math.min(deadline, timestampMillis(observation.activity.expiresAt))
    }
  }
  return deadline
}

export function retainedActivityRecords(state: ActivityState) {
  return state.seen.size + state.observations.size + state.terminalJobs.size
}
